<template>
  <div class="predefined-lot-number-form">
    <!-- Product selector trigger -->
    <div class="form-row">
      <div class="form-group col-sm-4 col-lg-4 text-left">
        <label>{{ $t("stock_management.predefined_lot_numbers.product") }}</label>
        <div
          class="sm-select-input-dialog-btn"
          @click="showProductDialog = true"
          :class="{ 'has-error': $v && $v.product_uuid && $v.product_uuid.$error }"
        >
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ selectedProductName || $t("common.select_placeholder") }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
        <ProductSelector
          ref="productSelector"
          :visible="showProductDialog"
          @close="showProductDialog = false"
          @continue="onProductSelected"
          :multiselect="false"
          :lot-management="true"
        />
      </div>
    </div>

    <!-- Lot number input -->
    <div class="form-row" style="margin-top: 12px">
      <div class="form-group col-sm-12 col-lg-4">
        <label>{{ $t("stock_management.predefined_lot_numbers.lot_number") }}</label>
        <DxTextBox
          :value="localValue.lot_number"
          :placeholder="$t('stock_management.predefined_lot_numbers.lot_number')"
          :is-valid="!($v && $v.lot_number && $v.lot_number.$error)"
          @value-changed="onLotNumberChanged"
        />
      </div>
    </div>

    <!-- Parcel selector trigger -->
    <div class="form-row" style="margin-top: 12px">
      <div class="form-group col-sm-12 col-lg-4">
        <label>{{ $t("stock_management.predefined_lot_numbers.parcel") }}</label>
        <div class="sm-select-input-dialog-btn" @click="showParcelDialog = true">
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ selectedParcelName || $t("common.select_placeholder") }}</div>
          </div>
          <span v-if="isEdit ? !!localValue.parcel_uuid : localParcelUuids.length > 0" @click.stop="clearParcel" style="float: right; color: #999; cursor: pointer">✕</span>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
        <ParcelSelector
          :visible="showParcelDialog"
          @close="showParcelDialog = false"
          @continue="onParcelSelected"
          :singleParcelDepartment="false"
          :multiselect="!isEdit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxSelectBox } from "devextreme-vue/select-box";
import ProductSelector from "@/components/ProductSelector.vue";
import ParcelSelector from "@/components/ParcelSelector.vue";

export default {
  name: "PredefinedLotNumberForm",
  components: {
    DxTextBox,
    DxSelectBox,
    ProductSelector,
    ParcelSelector,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    validations: {
      type: Object,
      default: null,
    },
    seasons: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValue: { ...this.value },
      showProductDialog: false,
      showParcelDialog: false,
      localParcelUuids: Array.isArray(this.value.parcel_uuids) ? [...this.value.parcel_uuids] : [],
      localParcelNames: Array.isArray(this.value.parcel_names) ? [...this.value.parcel_names] : [],
    };
  },
  computed: {
    $v() {
      return this.validations;
    },
    selectedProductName() {
      return this.localValue.product_name || null;
    },
    selectedParcelName() {
      if (this.isEdit) {
        return this.localValue.parcel_name || null;
      }
      if (this.localParcelNames.length > 0) {
        const first = this.localParcelNames.slice(0, 2).join(', ');
        const extra = this.localParcelNames.length - 2;
        return extra > 0 ? `${first} (+${extra})` : first;
      }
      return null;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.localValue = { ...newVal };
        if (!this.isEdit) {
          this.localParcelUuids = Array.isArray(newVal.parcel_uuids) ? [...newVal.parcel_uuids] : [];
          this.localParcelNames = Array.isArray(newVal.parcel_names) ? [...newVal.parcel_names] : [];
        }
      }
    },
  },
  methods: {
    ...mapActions({}),
    onProductSelected(products) {
      const product = Array.isArray(products) ? products[0] : products;
      if (!product) return;
      this.localValue = {
        ...this.localValue,
        product_uuid: product.uuid,
        product_name: product.name,
        product_code: product.code,
      };
      this.showProductDialog = false;
      this.$emit("input", { ...this.localValue });
    },
    onParcelSelected(parcels) {
      if (this.isEdit) {
        const parcel = Array.isArray(parcels) ? parcels[0] : parcels;
        if (!parcel) return;
        this.localValue = {
          ...this.localValue,
          parcel_uuid: parcel.uuid,
          parcel_name: parcel.name,
        };
        this.showParcelDialog = false;
        this.$emit("input", { ...this.localValue });
      } else {
        const parcelArray = Array.isArray(parcels) ? parcels : (parcels ? [parcels] : []);
        this.localParcelUuids = parcelArray.map((p) => p.uuid);
        this.localParcelNames = parcelArray.map((p) => p.name);
        this.localValue = {
          ...this.localValue,
          parcel_uuids: [...this.localParcelUuids],
          parcel_names: [...this.localParcelNames],
          parcel_name: this.localParcelNames.slice(0, 2).join(', ') + (this.localParcelNames.length > 2 ? ` (+${this.localParcelNames.length - 2})` : ''),
        };
        this.showParcelDialog = false;
        this.$emit("input", { ...this.localValue });
      }
    },
    clearParcel() {
      if (this.isEdit) {
        this.localValue = { ...this.localValue, parcel_uuid: null, parcel_name: null };
        this.$emit("input", { ...this.localValue });
      } else {
        this.localParcelUuids = [];
        this.localParcelNames = [];
        this.localValue = { ...this.localValue, parcel_uuids: [], parcel_names: [], parcel_name: null };
        this.$emit("input", { ...this.localValue });
      }
    },
    onLotNumberChanged(e) {
      this.localValue = { ...this.localValue, lot_number: e.value };
      this.$emit("input", { ...this.localValue });
    },
    validate() {
      const errors = [];
      if (!this.localValue.product_uuid) {
        errors.push(this.$t("common.required_field", { field: this.$t("stock_management.predefined_lot_numbers.product") }));
      }
      if (!this.localValue.lot_number || !this.localValue.lot_number.trim()) {
        errors.push(this.$t("common.required_field", { field: this.$t("stock_management.predefined_lot_numbers.lot_number") }));
      }
      return errors;
    },
  },
};
</script>
